import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Upload,
} from "antd";
import { debounce } from "lodash";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import {
  IPartNumberDtoInsert,
  IWarehouseLocation,
  IWarehouseLocationDetail,
} from "../../../models/Warehouse";
import { useInsertWarehouse } from "../../../query/WarehouseQuery";
import { warehouseService } from "../../../services/warehouseService";
import { RootState } from "../../../store";
import { notify } from "../../../utils/toast";

interface ModalAddWarehouseProps {
  open: boolean;
  onSuccess: (values: any[]) => void;
  onCancel: () => void;
}

const ModalAddWarehouse = ({
  open,
  onSuccess,
  onCancel,
}: ModalAddWarehouseProps) => {
  const [form] = Form.useForm();
  const [previewList, setPreviewList] = useState<IPartNumberDtoInsert[]>([]);
  const locations = useSelector(
    (state: RootState) => state.warehouse.locations,
  );
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number>();

  const insertWarehouse = useInsertWarehouse();
  const handleAdd = () => {
    form
      .validateFields()
      .then((values) => {
        if (
          previewList.length > 0 &&
          values.awb.toString() !== previewList[0].awb.toString()
        ) {
          notify.error("AWB phải giống với các items đã nhập trước đó");
          return;
        }
        setPreviewList((prev) => [...prev, values]);
        form.resetFields();
        form.setFieldValue("awb", values.awb);
      })
      .catch(() => {});
  };
  const handleOk = async () => {
    if (previewList.length === 0) {
      return;
    }
    const firstAwb = previewList[0].awb;
    const hasInvalidAwb = previewList.some((item) => item.awb !== firstAwb);
    if (hasInvalidAwb) {
      notify.error("Tất cả các items phải có cùng AWB");
      return;
    }
    await insertWarehouse.mutateAsync(previewList, {
      onSuccess: (data) => {
        setPreviewList([]);
        form.resetFields();
        onSuccess(Array.isArray(data) ? data : [data]);
        notify.success("Lưu thành công");
      },
      onError: (error) => {
        notify.error("Có lỗi xảy ra khi lưu");
      },
    });
  };
  const handleEdit = (idx: number) => {
    const itemToEdit = previewList[idx];
    const warehouse = locations.find(
      (w: IWarehouseLocation) => w.warehouseCode === itemToEdit.warehouseCode,
    );
    setSelectedWarehouseId(warehouse?.id);
    form.setFieldsValue(itemToEdit);
    setPreviewList((prev) => prev.filter((_, i) => i !== idx));
  };
  const handleRemove = (idx: number) => {
    setPreviewList((prev) => prev.filter((_, i) => i !== idx));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChangePartNumber = useCallback(
    debounce(async (value: string) => {
      const trimmedValue = value.trim();
      form.setFieldsValue({
        manufacturer: "",
        description: "",
        unit: "",
        project: "",
        internalCode: "",
      });
      if (!trimmedValue) return;

      try {
        const data =
          await warehouseService.getWarehouseByPartNumber(trimmedValue);

        if (data) {
          form.setFieldsValue({
            manufacturer: data[0].manufacturer,
            description: data[0].description,
            unit: data[0].unit,
            project: data[0].project,
            internalCode: data[0].internalCode,
          });
        }
      } catch (error) {}
    }, 300),
    [form],
  );

  const handleDownloadTemplate = () => {
    const headers = [
      "Dự án",
      "Mã dự án",
      "Part Number",
      "Nhà sản xuất",
      "Mô tả",
      "Đơn vị tính",
      "Số lượng hóa đơn",
      "Số lượng thực tế",
      "Diff",
      "AWB",
      "Mã kho",
      "Vị trí",
      "Mã nội bộ",
      "Note",
      "Note (Ext)",
      "Type",
      "Pur",
    ];

    const ws = XLSX.utils.aoa_to_sheet([headers, headers.map(() => "")]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Nhập kho");
    XLSX.writeFile(wb, "nhap_kho_template.xlsx");
  };
  const processExcelData = (data: any[]) => {
    try {
      const validData = data.filter(
        (row) =>
          row["Dự án"] && row["Part Number"] && row["AWB"] && row["Vị trí"],
      );
      if (validData.length === 0) {
        notify.error("File định dạng không hợp lệ");
        return;
      }
      const transformedData = validData.map((row) => {
        const location = locations.find(
          (wh) => wh.warehouseCode === row["Mã kho"],
        );

        const loc = location?.warehouseLocations.find(
          (whLoc) => whLoc.code === row["Vị trí"],
        );

        return {
          project: row["Dự án"],
          projectCode: row["Mã dự án"],
          partNumber: row["Part Number"],
          invoiceQuantity: Number(row["Số lượng hóa đơn"]) || 0,
          actualQuantity: Number(row["Số lượng thực tế"]) || 0,
          awb: row["AWB"],
          different: row["Diff"],
          manufacturer: row["Nhà sản xuất"],
          warehouseCode: row["Mã kho"],
          internalCode: row["Mã nội bộ"],
          unit: row["Đơn vị tính"],
          locationId: loc?.id || 0,
          description: row["Mô tả"],
          locationCode: loc?.code,
          note: row["Note"],
          noteExt: row["Note (Ext)"],
          supplierType: row["Type"],
          purchaser: row["Pur"],
        };
      });
      setPreviewList(transformedData);
      notify.success(`Đã thêm ${transformedData.length} dòng từ file Excel`);
    } catch (error) {
      notify.error(
        "Có lỗi xử lý dữ liệu Excel. Vui lòng kiểm tra định dạng file.",
      );
    }
  };

  const handleExcelUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        processExcelData(jsonData);
      } catch (error) {
        notify.error(
          "Có lỗi xử lý dữ liệu Excel. Vui lòng kiểm tra định dạng file.",
        );
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  return (
    <Modal
      open={open}
      title="Thêm mã hàng vào kho"
      onOk={handleOk}
      onCancel={() => {
        form.resetFields();
        setPreviewList([]);
        onCancel();
      }}
      confirmLoading={insertWarehouse.isPending}
      okText="Lưu tất cả"
      cancelText="Hủy"
      width="100vw"
      style={{ top: 0, maxWidth: "100vw" }}
      styles={{
        body: { height: "calc(100vh - 110px)", overflowY: "auto" },
      }}
    >
      <Form
        form={form}
        initialValues={{ invoiceQuantity: 0, actualQuantity: 0 }}
        style={{ marginBottom: 24 }}
      >
        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: "4px",
            overflow: "auto",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              tableLayout: "fixed",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "var(--bg-primary)" }}>
                <th
                  style={{
                    padding: "8px",
                    borderRight: "1px solid #d9d9d9",
                    borderBottom: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "140px",
                    textAlign: "center",
                  }}
                >
                  Tên dự án
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderRight: "1px solid #d9d9d9",
                    borderBottom: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "120px",
                    textAlign: "center",
                  }}
                >
                  Mã dự án
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderRight: "1px solid #d9d9d9",
                    borderBottom: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "120px",
                    textAlign: "center",
                  }}
                >
                  Mã hàng
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderRight: "1px solid #d9d9d9",
                    borderBottom: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "120px",
                    textAlign: "center",
                  }}
                >
                  Nhà sản xuất
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderBottom: "1px solid #d9d9d9",
                    borderRight: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "180px",
                    textAlign: "center",
                  }}
                >
                  Mô tả
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderRight: "1px solid #d9d9d9",
                    borderBottom: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "80px",
                    textAlign: "center",
                  }}
                >
                  Đơn vị
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderRight: "1px solid #d9d9d9",
                    borderBottom: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "100px",
                    textAlign: "center",
                  }}
                >
                  SL nhận
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderRight: "1px solid #d9d9d9",
                    borderBottom: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "100px",
                    textAlign: "center",
                  }}
                >
                  SL thực tế
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderRight: "1px solid #d9d9d9",
                    borderBottom: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "100px",
                    textAlign: "center",
                  }}
                >
                  Different
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderRight: "1px solid #d9d9d9",
                    borderBottom: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "100px",
                    textAlign: "center",
                  }}
                >
                  AWB
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderRight: "1px solid #d9d9d9",
                    borderBottom: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "100px",
                    textAlign: "center",
                  }}
                >
                  Mã kho
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderRight: "1px solid #d9d9d9",
                    borderBottom: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "100px",
                    textAlign: "center",
                  }}
                >
                  Vị trí
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderRight: "1px solid #d9d9d9",
                    borderBottom: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "100px",
                    textAlign: "center",
                  }}
                >
                  Mã nội bộ
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderBottom: "1px solid #d9d9d9",
                    borderRight: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "180px",
                    textAlign: "center",
                  }}
                >
                  Note
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderBottom: "1px solid #d9d9d9",
                    borderRight: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "100px",
                    textAlign: "center",
                  }}
                >
                  Note (Ext)
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderBottom: "1px solid #d9d9d9",
                    borderRight: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "100px",
                    textAlign: "center",
                  }}
                >
                  Type
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderBottom: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: "13px",
                    width: "100px",
                    textAlign: "center",
                  }}
                >
                  Purchaser
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  style={{
                    padding: "4px",
                    borderRight: "1px solid #d9d9d9",
                  }}
                >
                  <Form.Item
                    name="project"
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <Input placeholder="Tên dự án" size="small" />
                  </Form.Item>
                </td>
                <td
                  style={{
                    padding: "4px",
                    borderRight: "1px solid #d9d9d9",
                  }}
                >
                  <Form.Item
                    name="projectCode"
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <Input placeholder="Mã dự án" size="small" />
                  </Form.Item>
                </td>
                <td
                  style={{
                    padding: "4px",
                    borderRight: "1px solid #d9d9d9",
                  }}
                >
                  <Form.Item
                    name="partNumber"
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <Input
                      placeholder="Mã hàng"
                      size="small"
                      onChange={(e) => handleChangePartNumber(e.target.value)}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedText = e.clipboardData.getData("text");
                        form.setFieldValue("partNumber", pastedText);
                        handleChangePartNumber(pastedText);
                      }}
                    />
                  </Form.Item>
                </td>
                <td
                  style={{
                    padding: "4px",
                    borderRight: "1px solid #d9d9d9",
                  }}
                >
                  <Form.Item
                    name="manufacturer"
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <Input placeholder="Nhà sản xuất" size="small" />
                  </Form.Item>
                </td>
                <td
                  style={{ padding: "4px", borderRight: "1px solid #d9d9d9" }}
                >
                  <Form.Item name="description" style={{ margin: 0 }}>
                    <Input placeholder="Mô tả" size="small" />
                  </Form.Item>
                </td>
                <td
                  style={{
                    padding: "4px",
                    borderRight: "1px solid #d9d9d9",
                  }}
                >
                  <Form.Item
                    name="unit"
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <Input placeholder="Đơn vị" size="small" />
                  </Form.Item>
                </td>
                <td
                  style={{
                    padding: "4px",
                    borderRight: "1px solid #d9d9d9",
                  }}
                >
                  <Form.Item
                    name="invoiceQuantity"
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <InputNumber
                      min={0}
                      size="small"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </td>
                <td
                  style={{
                    padding: "4px",
                    borderRight: "1px solid #d9d9d9",
                  }}
                >
                  <Form.Item
                    name="actualQuantity"
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <InputNumber
                      min={0}
                      size="small"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </td>
                <td
                  style={{
                    padding: "4px",
                    borderRight: "1px solid #d9d9d9",
                  }}
                >
                  <Form.Item
                    name="different"
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <Input placeholder="Diff" size="small" />
                  </Form.Item>
                </td>
                <td
                  style={{
                    padding: "4px",
                    borderRight: "1px solid #d9d9d9",
                  }}
                >
                  <Form.Item
                    name="awb"
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <Input placeholder="AWB" size="small" />
                  </Form.Item>
                </td>
                <td
                  style={{
                    padding: "4px",
                    borderRight: "1px solid #d9d9d9",
                  }}
                >
                  <Form.Item
                    name="warehouseCode"
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <Select
                      placeholder="Mã kho"
                      size="small"
                      onChange={(_, option: any) => {
                        setSelectedWarehouseId(option.key);
                        form.setFieldValue("locationId", undefined);
                      }}
                    >
                      {locations
                        .filter(
                          (loc: IWarehouseLocation) =>
                            loc.isActive && loc.warehouseCode !== "QC",
                        )
                        .map((loc: IWarehouseLocation) => (
                          <Select.Option key={loc.id} value={loc.warehouseCode}>
                            {loc.warehouseCode}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                </td>
                <td
                  style={{
                    padding: "4px",
                    borderRight: "1px solid #d9d9d9",
                  }}
                >
                  <Form.Item
                    name="locationId"
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <Select
                      placeholder={
                        selectedWarehouseId ? "Vị trí" : "Chọn mã kho trước"
                      }
                      size="small"
                      disabled={!selectedWarehouseId}
                    >
                      {locations
                        // eslint-disable-next-line eqeqeq
                        .find((wh) => wh.id == selectedWarehouseId)
                        ?.warehouseLocations?.filter((loc) => loc.isActive)
                        .map((loc: IWarehouseLocationDetail) => (
                          <Select.Option key={loc.id} value={loc.id}>
                            {loc.code}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                </td>
                <td
                  style={{
                    padding: "4px",
                    borderRight: "1px solid #d9d9d9",
                  }}
                >
                  <Form.Item
                    name="internalCode"
                    rules={[{ required: true, message: "" }]}
                    style={{ margin: 0 }}
                  >
                    <Input placeholder="Mã nội bộ" size="small" />
                  </Form.Item>
                </td>
                <td
                  style={{ padding: "4px", borderRight: "1px solid #d9d9d9" }}
                >
                  <Form.Item name="note" style={{ margin: 0 }}>
                    <Input placeholder="Ghi chú" size="small" />
                  </Form.Item>
                </td>
                <td
                  style={{ padding: "4px", borderRight: "1px solid #d9d9d9" }}
                >
                  <Form.Item name="noteExt" style={{ margin: 0 }}>
                    <Input placeholder="Ghi chú (Ext)" size="small" />
                  </Form.Item>
                </td>
                <td
                  style={{ padding: "4px", borderRight: "1px solid #d9d9d9" }}
                >
                  <Form.Item name="supplierType" style={{ margin: 0 }}>
                    <Input placeholder="Loại NCC" size="small" />
                  </Form.Item>
                </td>
                <td style={{ padding: "4px" }}>
                  <Form.Item name="purchaser" style={{ margin: 0 }}>
                    <Input placeholder="Người mua" size="small" />
                  </Form.Item>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Space>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadTemplate}
                style={{ height: 40 }}
              >
                Tải mẫu Excel
              </Button>
              <Upload
                accept=".xlsx,.xls"
                showUploadList={false}
                beforeUpload={handleExcelUpload}
              >
                <Button icon={<UploadOutlined />} style={{ height: 40 }}>
                  Tải lên Excel
                </Button>
              </Upload>
            </Space>
          </Col>
          <Col span={12} style={{ textAlign: "right" }}>
            <Button
              type="primary"
              onClick={handleAdd}
              style={{ minWidth: 160, height: 40, fontSize: "16px" }}
            >
              Thêm vào danh sách
            </Button>
          </Col>
        </Row>
      </Form>
      <div
        style={{
          marginTop: 24,
          maxHeight: "calc(100vh - 400px)",
          overflowY: "auto",
        }}
      >
        <Table
          dataSource={previewList}
          rowKey={(row, idx) => idx + ""}
          pagination={false}
          bordered
          locale={{
            emptyText: "Không có dữ liệu",
          }}
          size="middle"
          scroll={{ x: "max-content", y: "calc(100vh - 500px)" }}
          style={{ minWidth: "100%" }}
          columns={[
            {
              title: "Action",
              key: "action",
              width: 60,
              render: (_: any, _row: any, idx: number) => (
                <Space>
                  <Popconfirm
                    title="Xóa dòng này?"
                    onConfirm={() => handleRemove(idx)}
                    okText="Xóa"
                    cancelText="Hủy"
                  >
                    <Button size="small" danger>
                      Xóa
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title="Sửa dòng này?"
                    onConfirm={() => handleEdit(idx)}
                    okText="Sửa"
                    cancelText="Hủy"
                  >
                    <Button size="small">Sửa</Button>
                  </Popconfirm>
                </Space>
              ),
            },
            { title: "Tên dự án", dataIndex: "project", align: "center" },
            { title: "Mã dự án", dataIndex: "projectCode", align: "center" },
            { title: "Part Number", dataIndex: "partNumber", align: "center" },
            {
              title: "Nhà sản xuất",
              dataIndex: "manufacturer",
              align: "center",
            },
            { title: "Mô tả", dataIndex: "description", align: "center" },
            { title: "Đơn vị tính", dataIndex: "unit", align: "center" },
            {
              title: "Invoice Quantity",
              dataIndex: "invoiceQuantity",
              align: "center",
            },
            {
              title: "Actual Quantity",
              dataIndex: "actualQuantity",
              align: "center",
            },
            {
              title: "Different",
              dataIndex: "different",
              align: "center",
            },
            { title: "AWB", dataIndex: "awb", align: "center" },
            { title: "Mã kho", dataIndex: "warehouseCode", align: "center" },
            {
              title: "Vị trí",
              dataIndex: "locationId",
              align: "center",
              render: (value, record) => {
                const location = locations.find(
                  (wh) => wh.warehouseCode === record.warehouseCode,
                );
                const loc = location?.warehouseLocations.find(
                  (wh) => wh.id === value,
                );
                return loc?.code || "N/A";
              },
            },
            { title: "Mã nội bộ", dataIndex: "internalCode", align: "center" },
            { title: "Ghi chú", dataIndex: "note", align: "center" },
            {
              title: "Ghi chú (Ext)",
              dataIndex: "noteExt",
              align: "center",
            },
            {
              title: "Loại NCC",
              dataIndex: "supplierType",
              align: "center",
            },
            { title: "Người mua", dataIndex: "purchaser", align: "center" },
          ]}
        />
      </div>
    </Modal>
  );
};

export default ModalAddWarehouse;
